import {
  CommentProposalPayload,
  InlineCommentCancelledHandler,
  InlineCommentProposedHandler,
} from '@amzn/awscat-editor/dist/extensions/commentProposal';
import { Editor } from '@tiptap/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInlineCommentManager } from '../../../contexts/InlineCommentManagerContext';
import { docIdToCommentPath, InlineTextFilter } from '../../../services/InlineTextOperations';
import { resolveCoordsAtPos } from './EditorCoords';
import { scrollIntoComfortZone } from './Window';
import { useStableDocId } from './useStableDocId';

/** Position and text range of an in-progress comment proposal popover. */
export interface CommentProposalState {
  readonly target: DOMRect;
  readonly from: number;
  readonly to: number;
  readonly head: number;
  readonly textHint: string;
}

/** Handlers and state returned by useCommentProposalIntegration. */
export interface CommentProposalIntegration {
  readonly proposalState: CommentProposalState | undefined;
  readonly handleProposed: InlineCommentProposedHandler;
  readonly handleCancelled: InlineCommentCancelledHandler;
  readonly dismissProposal: () => void;
  readonly submitComment: (body: string) => Promise<void>;
}

function computeTargetRect(editor: Editor, head: number): DOMRect | undefined {
  if (editor.isDestroyed) return undefined;
  try {
    const coords = resolveCoordsAtPos(editor.view, head);
    return new DOMRect(
      coords.left + window.scrollX,
      coords.top + window.scrollY,
      0,
      coords.bottom - coords.top,
    );
  } catch {
    // head may exceed doc size after edits shrink the document
    return undefined;
  }
}

/** Integrates Tiptap's comment-proposal extension with InlineCommentManager to drive inline comment creation. */
export function useCommentProposalIntegration(
  editor: Editor | undefined,
  inlineTextFilter: InlineTextFilter | undefined,
  onHideOtherPopovers?: () => void,
): CommentProposalIntegration {
  const [proposalState, setProposalState] = useState<CommentProposalState | undefined>(undefined);
  const proposalRef = useRef<CommentProposalState | undefined>(undefined);
  proposalRef.current = proposalState;
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const inlineCommentManager = useInlineCommentManager();
  const docId = useStableDocId(inlineTextFilter);

  // When the editor fires a comment proposal, compute the popover position and store state.
  const handleProposed: InlineCommentProposedHandler = useCallback(
    (payload: CommentProposalPayload) => {
      if (!editor || editor.isDestroyed) return;

      const target = computeTargetRect(editor, payload.head);
      if (!target) return;

      onHideOtherPopovers?.();

      const state: CommentProposalState = {
        target,
        from: payload.from,
        to: payload.to,
        head: payload.head,
        textHint: payload.textHint,
      };

      // Delay showing the popup to avoid flicker on transient selections.
      clearTimeout(showTimerRef.current);
      showTimerRef.current = setTimeout(() => {
        setProposalState(state);
        scrollIntoComfortZone(target);
      }, 300);
    },
    [editor, onHideOtherPopovers],
  );

  // When the editor cancels the proposal (deselect/escape).
  const handleCancelled: InlineCommentCancelledHandler = useCallback(() => {
    clearTimeout(showTimerRef.current);
    setProposalState(undefined);
  }, []);

  const dismissProposal = useCallback(() => {
    clearTimeout(showTimerRef.current);
    setProposalState(undefined);
  }, []);

  // When the user submits a comment from the popover.
  const submitComment = useCallback(
    async (body: string): Promise<void> => {
      const current = proposalRef.current;
      if (!current || !docId) {
        throw new Error('Cannot submit comment: no active proposal or document context.');
      }

      const commentId = await inlineCommentManager.createComment({
        kind: 'new-thread',
        sectionPath: docIdToCommentPath(docId),
        anchor: {
          startOffset: current.from,
          endOffset: current.to,
          text: current.textHint,
        },
        body,
      });

      setProposalState(undefined);
      inlineCommentManager.selectThread(commentId);
      inlineCommentManager.revealInSidebar(commentId);
    },
    [docId, inlineCommentManager],
  );

  // When the proposal is active, recalculate position on resize and editor update.
  useEffect(() => {
    if (!proposalState || !editor || editor.isDestroyed) return;

    const { head } = proposalState;
    const recalc = () => {
      const target = computeTargetRect(editor, head);
      if (!target) {
        setProposalState(undefined);
        return;
      }
      setProposalState((prev) => (prev ? { ...prev, target } : undefined));
    };

    editor.on('update', recalc);
    window.addEventListener('resize', recalc);
    return () => {
      editor.off('update', recalc);
      window.removeEventListener('resize', recalc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalState ? proposalState.head : null, editor]);

  return {
    proposalState,
    handleProposed,
    handleCancelled,
    dismissProposal,
    submitComment,
  };
}
